/** @jsxImportSource @emotion/react */
import { Box, Button, Container, Step, StepLabel, Stepper } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useIconOptions } from '@app/modules/Home/Data/hooks/useIconOptions'
import { useUploadForm } from '@app/modules/Home/Data/hooks/useUploadForm'
import { updateDataset } from '@app/services/dataUploadService'
import ElementAttributesStep from './ElementAttributesStep'
import ModeChooser from './ModeChooser'
import UploadFormStep from './UploadFormStep'
import { pageWrapper, container, formWrapper } from './DataUpload.styles'
import { GuidePageHeader } from '@app/modules/GuideV2/components/GuidePageHeader'

type Mode = 'choose' | 'upload'

const STEPS = ['Upload Element Dataset', 'Configure Element Attributes']

const HomeDataUpload = () => {
  const [mode, setMode] = useState<Mode>('choose')
  const navigate = useNavigate()

  const iconOptions = useIconOptions()
  const upload = useUploadForm()

  const handleModeChange = (next: Mode) => {
    setMode(next)
    upload.handleReset()
  }

  return (
    <Box css={pageWrapper}>
      <Box css={container}>
        <GuidePageHeader
          header="Element Data Upload"
          subheader="Upload file based element datasets"
        />
        <Box css={formWrapper}>
          <Container>
            {mode === 'choose' && (
              <ModeChooser
                onSelect={(m) => {
                  if (m === 'update') {
                    navigate('/app/home/data-upload/update')
                  } else {
                    handleModeChange(m)
                  }
                }}
              />
            )}

            {mode === 'upload' && (
              <>
                {upload.activeStep === 0 && (
                  <Button
                    variant="text"
                    onClick={() => handleModeChange('choose')}
                    sx={{ mb: 2, pl: 0 }}
                  >
                    ← Back
                  </Button>
                )}
                <Stepper activeStep={upload.activeStep} sx={{ mb: 4 }}>
                  {STEPS.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                {upload.activeStep === 1 ? (
                  <ElementAttributesStep
                    gdalInfo={upload.gdalInfo}
                    gdalFailed={upload.gdalFailed}
                    gdalSkipped={upload.gdalSkipped}
                    onReset={upload.handleReset}
                    onUploadConfig={
                      upload.datasetId
                        ? async (config) => {
                            await updateDataset(upload.datasetId!, { config })
                            handleModeChange('choose')
                          }
                        : undefined
                    }
                  />
                ) : (
                  <UploadFormStep
                    inputRef={upload.inputRef}
                    register={upload.register}
                    control={upload.control}
                    errors={upload.errors}
                    selectedFiles={upload.selectedFiles}
                    geometryType={upload.geometryType}
                    iconOptions={iconOptions}
                    onGeometryChange={upload.handleGeometryChange}
                    onSubmit={upload.handleSubmit(upload.onSubmit)}
                    uploading={upload.uploading}
                    extracting={upload.extracting}
                    progress={upload.progress}
                    status={upload.status}
                    errorMessage={upload.errorMessage}
                    isSubmitting={upload.isSubmitting}
                  />
                )}
              </>
            )}
          </Container>
        </Box>
      </Box>
    </Box>
  )
}

export default HomeDataUpload
